import { memoizeFunction } from '@agilepoint/utils-common';
import { getMaxDate, getMinDate, getNumberOfDaysInMonth, getDateTimeFromDateParts, IDate, IDateFormat, getDateParts } from '@agilepoint/utils-date-time';
import { Day, IDateInfo } from './calendar-types';
import { IDropdownOption } from '../dropdown';

const MAX_DATE = getMaxDate();
const MIN_DATE = getMinDate();

const START_YEAR = MIN_DATE.getFullYear();
const END_YEAR = MAX_DATE.getFullYear();

export const getYearOptions = memoizeFunction((startYear = START_YEAR, endYear = END_YEAR) => {
    const YEAR_OPTIONS: IDropdownOption[] = [];
    for (let i = startYear; i <= endYear; i++) {
        const year = i.toString();
        YEAR_OPTIONS.push({ key: i, text: year });
    }
    return YEAR_OPTIONS;
});

export function getStartDay(month: number, year: number) {
    return getDateTimeFromDateParts({ month, year, date: 1 }).weekday;
}

function getDateInfoForPrevMonth(currentMonth: number, year: number, weekStartDay: Day): IDateInfo[] {
    let prevMonth = currentMonth - 1;
    if (prevMonth === 0) {
        prevMonth = 12;
    }
    const startDay = getStartDay(currentMonth, year);
    const availableSlotsForPrevMonth = Math.abs(7 - weekStartDay + startDay) % 7;
    if (!availableSlotsForPrevMonth) {
        return [];
    }
    const totalNumberOfDays = getNumberOfDaysInMonth(prevMonth, year);
    const dateInfo: IDateInfo[] = [];
    for (let i = availableSlotsForPrevMonth - 1; i >= 0; i--) {
        dateInfo.push({
            date: totalNumberOfDays - i,
            month: prevMonth,
            year: prevMonth !== 12 ? year : year - 1,
            isInCurentMonth: false
        });
    }

    return dateInfo;
}

function getDateInfoForNextMonth(availableSlots: number, currentMonth: number, year: number): IDateInfo[] {
    const dateInfo: IDateInfo[] = [];
    let nextMonth = currentMonth + 1;
    if (nextMonth > 12) {
        nextMonth = 1;
    }
    for (let date = 1; date <= availableSlots; date++) {
        dateInfo.push({
            date,
            month: nextMonth,
            year: nextMonth !== 0 ? year : year + 1,
            isInCurentMonth: false
        });
    }

    return dateInfo;
}

function createSegmentedData(list: IDateInfo[][], item: IDateInfo, idx: number) {
    const rowNumber = Math.floor(idx / 7);
    let row = list[rowNumber];
    if (row === undefined) {
        row = list[rowNumber] = [];
    }
    row.push(item);
    list[rowNumber] = row;
    return list;
}

// TODO: Implement shallow comaparision in memoizeFunction and pass argument as object in the below function
export const prepareCalendarData = memoizeFunction((month: number, year: number, weekStartDay: Day): IDateInfo[][] => {
    const dateInfo: IDateInfo[] = [];
    const totalNumberOfDays = getNumberOfDaysInMonth(month, year);
    for (let date = 1; date <= totalNumberOfDays; date++) {
        dateInfo.push({
            date,
            month,
            year,
            isInCurentMonth: true
        });
    }
    const prevMonthDateInfo = getDateInfoForPrevMonth(month, year, weekStartDay);
    const totalAvailableSlots = Math.ceil((prevMonthDateInfo.length + dateInfo.length) / 7) * 7;
    const concatinatedInfo = prevMonthDateInfo.concat(dateInfo).concat(getDateInfoForNextMonth(totalAvailableSlots - prevMonthDateInfo.length - dateInfo.length, month, year));
    return concatinatedInfo.reduce(createSegmentedData, []);
});

export function getNextMonthInfo(currentMonth: number, currentYear: number, step = 1) {
    const newMonth = currentMonth + step;
    if (newMonth > 12) {
        return {
            month: newMonth % 12,
            year: currentYear + 1
        };
    }
    return { month: newMonth, year: currentYear };
}

export function getPrevMonthInfo(currentMonth: number, currentYear: number, step = 1) {
    const newMonth = currentMonth - step;
    if (newMonth < 1) {
        return {
            month: 12 + newMonth,
            year: currentYear - 1
        };
    }
    return { month: newMonth, year: currentYear };
}

export function isSameDate(format: IDateFormat, date1?: IDate, date2?: IDate) {
    if (!(date1 && date2)) {
        return false;
    }
    try {
        const { date, month, year } = getDateParts(date1, format);
        const dateParts2 = getDateParts(date2, format);
        return dateParts2.date === date && dateParts2.month === month && dateParts2.year === year;
    } catch (err) {
        return false;
    }
}

export const excludeTimeComponent = memoizeFunction((date?: IDate, format?: IDateFormat) => date ? getDateTimeFromDateParts(getDateParts(date, format)) : date);
